import React, { useEffect, useState } from 'react';
import { feeAPI, courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiBell, FiPlus, FiTrash2, FiRefreshCw, FiSend } from 'react-icons/fi';
import Select from 'react-select';

const tabs = [
  { id: 'settings', label: 'Reminder settings' },
  { id: 'dues', label: 'Student fee dues' },
  { id: 'logs', label: 'Send log' }
];

const FeeReminders = () => {
  const [tab, setTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dues, setDues] = useState([]);
  const [logs, setLogs] = useState([]);
  const [students, setStudents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [dueForm, setDueForm] = useState({
    student_id: '',
    title: '',
    due_date: '',
    amount: '',
    notes: ''
  });
  const [forceRun, setForceRun] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (tab === 'dues') loadDues();
    if (tab === 'logs') loadLogs();
    if (tab === 'dues' && students.length === 0) loadStudents();
    if (tab === 'settings') loadDiagnostics();
  }, [tab]);

  const loadDiagnostics = async () => {
    try {
      const res = await feeAPI.getReminderDiagnostics();
      if (res.data.success) setDiagnostics(res.data.data);
    } catch (e) {
      setDiagnostics(null);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await feeAPI.getReminderSettings();
      if (res.data.success) setSettings(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const loadDues = async () => {
    try {
      const res = await feeAPI.getStudentDues();
      if (res.data.success) setDues(res.data.data);
    } catch (e) {
      toast.error('Could not load fee dues');
    }
  };

  const loadLogs = async () => {
    try {
      const res = await feeAPI.getReminderLogs({ limit: 80 });
      if (res.data.success) setLogs(res.data.data);
    } catch (e) {
      toast.error('Could not load logs');
    }
  };

  const loadStudents = async () => {
    try {
      const res = await courseAPI.getStudents({ per_page: 500, page: 1 });
      if (res.data.success) setStudents(res.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await feeAPI.updateReminderSettings(settings);
      if (res.data.success) {
        toast.success(res.data.message || 'Saved');
        setSettings(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    const msg = forceRun
      ? 'Send reminders for ALL pending fee dues now (ignores due-date windows)?'
      : 'Run reminder job using due-date rules only (emails only if today matches e.g. “3 days before”)?';
    if (!window.confirm(msg)) return;
    try {
      const res = await feeAPI.runRemindersNow({ force: forceRun });
      if (res.data.success) {
        toast.success(res.data.message || 'Done');
        const s = res.data.data || {};
        toast.info(
          `sent: ${s.sent ?? 0}, skipped: ${s.skipped ?? 0}, failed: ${s.failed ?? 0}, ` +
            `no_window_today: ${s.no_reminder_today ?? 0}, email_off: ${s.email_skipped_disabled ?? 0}, no_student_email: ${s.email_skipped_no_address ?? 0}`
        );
        loadLogs();
        loadDiagnostics();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Run failed');
    }
  };

  const submitDue = async (e) => {
    e.preventDefault();
    try {
      const res = await feeAPI.createStudentDue({
        student_id: dueForm.student_id,
        title: dueForm.title,
        due_date: dueForm.due_date,
        amount: Number(dueForm.amount) || 0,
        notes: dueForm.notes || null
      });
      if (res.data.success) {
        toast.success('Fee due added');
        setShowAdd(false);
        setDueForm({ student_id: '', title: '', due_date: '', amount: '', notes: '' });
        loadDues();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add');
    }
  };

  const markPaid = async (row) => {
    try {
      const res = await feeAPI.updateStudentDue(row.id, {
        status: 'paid',
        amount_paid: row.amount
      });
      if (res.data.success) {
        toast.success('Marked paid');
        loadDues();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const removeDue = async (id) => {
    if (!window.confirm('Delete this fee due?')) return;
    try {
      const res = await feeAPI.deleteStudentDue(id);
      if (res.data.success) {
        toast.success('Deleted');
        loadDues();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const studentOptions = students.map((s) => ({
    value: s.id,
    label: `${s.firstname || ''} ${s.lastname || ''} (${s.email || s.mobileno || s.id})`.trim()
  }));

  if (loading || !settings) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  const setField = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <FiBell className="text-primary-600" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fee reminders</h1>
          <p className="text-sm text-gray-600">
            Email, SMS (MSG91 / Twilio), and WhatsApp templates — before due dates and optional overdue notices.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
              tab === t.id
                ? 'bg-white text-primary-600 border border-b-0 border-gray-200'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' && (
        <form onSubmit={saveSettings} className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <strong>Why no email?</strong> The scheduled job only sends on specific calendar days (for example, 3 days before each
            due date). “Run reminders now” does the same unless you check <strong>force</strong> below — then every pending due
            gets one send (same day duplicates are skipped).
          </div>

          {diagnostics && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-800">Diagnostics (today: {diagnostics.today})</span>
                <button
                  type="button"
                  onClick={loadDiagnostics}
                  className="text-primary-600 text-xs hover:underline"
                >
                  Refresh
                </button>
              </div>
              <p>
                Pending fee dues: <strong>{diagnostics.pending_dues_count}</strong> · Email channel:{' '}
                <strong>{diagnostics.email_enabled ? 'on' : 'off'}</strong> · Students missing email:{' '}
                <strong>{diagnostics.students_without_email_count}</strong>
              </p>
              {diagnostics.dues?.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                  {diagnostics.dues.map((d) => (
                    <li key={d.due_id}>
                      #{d.due_id} {d.title} (due {d.due_date}) — student email:{' '}
                      {d.student_has_email ? 'yes' : <span className="text-red-600 font-medium">no</span>}
                      {d.reminder_windows?.some((w) => w.is_today) ? (
                        <span className="text-green-700"> · sends today</span>
                      ) : (
                        <span className="text-gray-600"> · no reminder window today (use force)</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.is_enabled}
              onChange={(e) => setField('is_enabled', e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-800">Enable automated fee reminders</span>
          </label>
          <p className="text-xs text-gray-500">
            Scheduler runs daily at 09:00 server time. Add a cron: <code className="bg-gray-100 px-1">* * * * * php artisan schedule:run</code>
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Days before due (comma-separated)</label>
            <input
              type="text"
              value={settings.remind_days || '3,1,0'}
              onChange={(e) => setField('remind_days', e.target.value)}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2"
              placeholder="3,1,0"
            />
            <p className="text-xs text-gray-500 mt-1">Use 0 for a reminder on the due date itself.</p>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.send_overdue}
              onChange={(e) => setField('send_overdue', e.target.checked)}
            />
            <span className="text-sm text-gray-800">Send overdue reminders (every N days while unpaid)</span>
          </label>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">Overdue interval (days)</label>
            <input
              type="number"
              min={1}
              max={90}
              value={settings.overdue_every_days ?? 7}
              onChange={(e) => setField('overdue_every_days', parseInt(e.target.value, 10) || 7)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="border-t pt-4 space-y-2">
            <p className="text-sm font-semibold text-gray-900">Channels</p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.email_enabled}
                onChange={(e) => setField('email_enabled', e.target.checked)}
              />
              <span className="text-sm">Email (uses student email)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.sms_enabled}
                onChange={(e) => setField('sms_enabled', e.target.checked)}
              />
              <span className="text-sm">SMS</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.whatsapp_enabled}
                onChange={(e) => setField('whatsapp_enabled', e.target.checked)}
              />
              <span className="text-sm">WhatsApp (Meta template with one body variable)</span>
            </label>
          </div>

          <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMS provider</label>
              <select
                value={settings.sms_provider || ''}
                onChange={(e) => setField('sms_provider', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">— None —</option>
                <option value="msg91">MSG91</option>
                <option value="twilio">Twilio</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MSG91 auth key</label>
              <input
                type="password"
                value={settings.sms_auth_key || ''}
                onChange={(e) => setField('sms_auth_key', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MSG91 sender ID</label>
              <input
                type="text"
                value={settings.sms_sender_id || ''}
                onChange={(e) => setField('sms_sender_id', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twilio Account SID</label>
              <input
                type="text"
                value={settings.twilio_sid || ''}
                onChange={(e) => setField('twilio_sid', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twilio auth token</label>
              <input
                type="password"
                value={settings.twilio_token || ''}
                onChange={(e) => setField('twilio_token', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twilio From (E.164)</label>
              <input
                type="text"
                placeholder="+15551234567"
                value={settings.twilio_from || ''}
                onChange={(e) => setField('twilio_from', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp phone number ID</label>
              <input
                type="text"
                value={settings.whatsapp_phone_id || ''}
                onChange={(e) => setField('whatsapp_phone_id', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp access token</label>
              <input
                type="password"
                value={settings.whatsapp_token || ''}
                onChange={(e) => setField('whatsapp_token', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp template name</label>
              <input
                type="text"
                placeholder="fee_reminder"
                value={settings.whatsapp_template_name || ''}
                onChange={(e) => setField('whatsapp_template_name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template language code</label>
              <input
                type="text"
                value={settings.whatsapp_template_lang || 'en'}
                onChange={(e) => setField('whatsapp_template_lang', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 border-t">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={forceRun}
                onChange={(e) => setForceRun(e.target.checked)}
              />
              <span>
                <strong>Force:</strong> send to all pending dues now (ignore due-date windows). Use this to test mail delivery.
              </span>
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <FiRefreshCw /> {saving ? 'Saving…' : 'Save settings'}
              </button>
              <button
                type="button"
                onClick={handleRunNow}
                className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
              >
                <FiSend /> Run reminders now {forceRun ? '(forced)' : '(date rules)'}
              </button>
            </div>
          </div>
        </form>
      )}

      {tab === 'dues' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Create a row per student fee obligation. Reminders use balance (amount − paid).
            </p>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm"
            >
              <FiPlus /> Add fee due
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2">Student</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Due</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Paid</th>
                  <th className="px-4 py-2">Balance</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {dues.map((d) => (
                  <tr key={d.id} className="border-t border-gray-100">
                    <td className="px-4 py-2">{d.student_name || d.student_id}</td>
                    <td className="px-4 py-2">{d.title}</td>
                    <td className="px-4 py-2">{d.due_date}</td>
                    <td className="px-4 py-2">{d.amount}</td>
                    <td className="px-4 py-2">{d.amount_paid}</td>
                    <td className="px-4 py-2 font-medium">{Number(d.balance).toFixed(2)}</td>
                    <td className="px-4 py-2">{d.status}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      {d.status === 'pending' && Number(d.balance) > 0 && (
                        <button
                          type="button"
                          onClick={() => markPaid(d)}
                          className="text-primary-600 hover:underline"
                        >
                          Mark paid
                        </button>
                      )}
                      <button type="button" onClick={() => removeDue(d.id)} className="text-red-600">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
                {dues.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No fee dues yet. Add students’ obligations to start reminders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {showAdd && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <h3 className="text-lg font-semibold mb-4">Add fee due</h3>
                <form onSubmit={submitDue} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                    <Select
                      options={studentOptions}
                      value={studentOptions.find((o) => o.value === dueForm.student_id) || null}
                      onChange={(opt) => setDueForm((f) => ({ ...f, student_id: opt?.value || '' }))}
                      placeholder="Select student"
                      isClearable
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      required
                      value={dueForm.title}
                      onChange={(e) => setDueForm((f) => ({ ...f, title: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g. March tuition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
                    <input
                      required
                      type="date"
                      value={dueForm.due_date}
                      onChange={(e) => setDueForm((f) => ({ ...f, due_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      required
                      type="number"
                      min="0"
                      step="0.01"
                      value={dueForm.amount}
                      onChange={(e) => setDueForm((f) => ({ ...f, amount: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={dueForm.notes}
                      onChange={(e) => setDueForm((f) => ({ ...f, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAdd(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'logs' && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Due #</th>
                <th className="px-4 py-2">Channel</th>
                <th className="px-4 py-2">Key</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Recipient</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap">{l.created_at}</td>
                  <td className="px-4 py-2">{l.student_fee_due_id}</td>
                  <td className="px-4 py-2">{l.channel}</td>
                  <td className="px-4 py-2">{l.reminder_key}</td>
                  <td className="px-4 py-2">{l.status}</td>
                  <td className="px-4 py-2 max-w-xs truncate">{l.recipient}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No sends logged yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeeReminders;
